import React from "react";
import styled from "styled-components";

import docIcon from "../../../assets/icons/doc-icon.svg";
import jpgIcon from "../../../assets/icons/jpg-icon.svg";
import pdfIcon from "../../../assets/icons/pdf-icon.svg";
import pngIcon from "../../../assets/icons/png-icon.svg";
import pptIcon from "../../../assets/icons/ppt-icon.svg";
import xlsxIcon from "../../../assets/icons/xlsx-icon.svg";
import mp4Icon from "../../../assets/icons/mp4-icon.svg";
import othIcon from "../../../assets/icons/oth.png";

const normalizeFileType = (extension) => {
  if (!extension) return "";
  extension = extension.toLowerCase();

  const fileTypeMap = {
    // Documents
    ppt: ["ppt", "pptx", "powerpoint"],
    word: ["doc", "docx", "word"],
    pdf: ["pdf"],

    // Spreadsheets
    xlsx: ["xls", "xlsx", "excel", "csv"],

    // Images
    jpg: ["jpg", "jpeg", "image", "images"],
    png: ["png"],
    gif: ["gif"],
    webp: ["webp"],
    svg: ["svg"],

    // Videos - Individual types
    mp4: ["mp4", "mpeg4"],

    // Videos - Category
    video: ["video", "videos"],

    // Audio
    mp3: ["mp3", "mpeg3"],
    wav: ["wav", "wave"],

    // Archives
    zip: ["zip"],
    rar: ["rar"],

    // Text
    txt: ["txt", "text"],
  };

  for (const [normalizedType, extensions] of Object.entries(fileTypeMap)) {
    if (extensions.includes(extension)) {
      return normalizedType;
    }
  }

  return extension;
};

const getFileIcon = (extension) => {
  if (!extension) return othIcon;

  const normalizedExt = normalizeFileType(extension);

  switch (normalizedExt) {
    case "pdf":
      return pdfIcon;
    case "ppt":
      return pptIcon;
    case "word":
      return docIcon;
    case "jpg":
      return jpgIcon;
    case "png":
      return pngIcon;
    case "gif":
      return pngIcon;
    case "webp":
      return pngIcon;
    case "svg":
      return pngIcon;
    case "image":
      return jpgIcon;
    case "xlsx":
      return xlsxIcon;
    case "mp4":
      return mp4Icon;
    case "video":
      return mp4Icon;
    default:
      return othIcon;
  }
};

function timeFormatter(dateString) {
  const date = new Date(dateString);
  const formattedTime = new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  return formattedTime;
}

const NormalSubmissionSubmittedFile = ({ submittedFiles }) => {
  return (
    <>
      <SubmittedFilesTitle>Submitted Files</SubmittedFilesTitle>
      <LoadedFilesDiv>
        <FilesGrid>
          {submittedFiles
            .sort(
              (a, b) => new Date(b.submitted_date) - new Date(a.submitted_date)
            )
            .map((item, index) => (
              <SubmissionDetails key={index}>
                {item.feedback && (
                  <FeedbackSection>
                    <FeedbackLabel>
                      Feedback
                      {item.feedback_date && (
                        <span
                          style={{
                            fontWeight: "400",
                            fontSize: "12px",
                            color: "#656565",
                            marginLeft: "10px",
                            marginBottom: "5px",
                          }}
                        >
                          {timeFormatter(item.feedback_date)}
                        </span>
                      )}
                    </FeedbackLabel>
                    {item.feedback && (
                      <FeedbackDisplay>{item.feedback}</FeedbackDisplay>
                    )}
                  </FeedbackSection>
                )}

                {item.upload && (
                  <FacultyFeedbackSection>
                    <FileSectionTitle>
                      Faculty Feedback File
                      <FileSectionSubTitle>
                        {" "}
                        (Download the file to view the detailed faculty
                        feedback.){" "}
                      </FileSectionSubTitle>
                    </FileSectionTitle>

                    <SubmittedFilesGrid>
                      <SubmittedFileCard
                        href={item.upload}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FileIconWrapper>
                          <img
                            src={getFileIcon(item.feedback_feedback_type)}
                            alt={`${item.feedback_feedback_type} file`}
                          />
                        </FileIconWrapper>
                        <SubmittedFileDetails>
                          <SubmittedFileName>
                            {item.feedback_file_name}
                          </SubmittedFileName>
                          <SubmittedFileType>
                            {item.feedback_feedback_type}
                          </SubmittedFileType>
                        </SubmittedFileDetails>
                      </SubmittedFileCard>
                    </SubmittedFilesGrid>
                  </FacultyFeedbackSection>
                )}
                <FilesSection>
                  <div>
                    <FileSectionTitle>
                      Student File
                      <span
                        style={{
                          fontWeight: "400",
                          fontSize: "12px",
                          color: "#656565",
                          marginLeft: "10px",
                          marginBottom: "5px",
                        }}
                      >
                        {timeFormatter(item.submitted_date)}
                      </span>
                    </FileSectionTitle>

                    <SubmittedFilesGrid>
                      {item.files.map((file, fileIndex) => (
                        <SubmittedFileCard
                          key={fileIndex}
                          href={file.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FileIconWrapper>
                            <img
                              src={getFileIcon(file.file_type)}
                              alt={`${file.file_type} file`}
                            />
                          </FileIconWrapper>
                          <SubmittedFileDetails>
                            <SubmittedFileName>
                              {file.file_name}
                            </SubmittedFileName>
                            <SubmittedFileType>
                              {file.file_type}
                            </SubmittedFileType>
                          </SubmittedFileDetails>
                        </SubmittedFileCard>
                      ))}
                    </SubmittedFilesGrid>
                  </div>
                </FilesSection>
              </SubmissionDetails>
            ))}
        </FilesGrid>
      </LoadedFilesDiv>
    </>
  );
};

export default NormalSubmissionSubmittedFile;

const SubmittedFilesTitle = styled.div`
  width: 100%;
  text-align: start;
  color: #828282ff;
  margin: 25px 0 15px 10px;
`;
const FeedbackLabel = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #1e293b;
  margin-bottom: 8px;
`;

const SubmissionDetails = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 10px 18px 16px 18px;
  width: 100%;
`;
const FeedbackSection = styled.div`
  margin-bottom: 1rem;
`;

const FeedbackDisplay = styled.div`
  padding: 12px 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  line-height: 1.5;
`;
const FacultyFeedbackSection = styled.div`
  margin-top: 16px;
  margin-bottom: 1rem;
`;
const FileSectionTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #1e293b;
  margin-bottom: 12px;
  line-height: 1.4;
`;
const FileSectionSubTitle = styled.span`
  font-weight: 400;
  font-size: 14px;
  color: #64748b;
  margin-left: 2px;
`;
const SubmittedFilesGrid = styled.div`
  display: flex;
  // grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  width:100%;
  flex-wrap: wrap;
  gap: 12px;
`;

const SubmittedFileCard = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  cursor: pointer;
  // width: calc(33% - 6px);
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 600px) {
    width: 100%;
  }

  &:hover {
    background: linear-gradient(to right, #f0f4ff, #e8f0ff);
    border-color: #c7d2fe;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
  }
`;
const FileIconWrapper = styled.div`
  flex-shrink: 0;

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
`;
const SubmittedFileDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const SubmittedFileName = styled.div`
  font-weight: 500;
  font-size: 13px;
  color: #1f2937;
  margin-bottom: 2px;
  max-width:200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubmittedFileType = styled.div`
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
const FilesSection = styled.div`
  margin-bottom: 20px;
  display: flex;
  gap: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const LoadedFilesDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin-top: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const FilesGrid = styled.div`
  width: 100%;
  gap: 10px;
  display: flex;
  flex-wrap: wrap;
`;
